import { Loader } from "@sazka/design-system/src/loader/loader";
import { computed, nextTick, ref, watch } from "vue";
import { AnalyticsHelper } from "../analytics/analytics-helper";
import { LotteryCheckout } from "./lottery-checkout";
import { useLotteryCheckoutStore } from "./lottery-checkout-store";
import { showAlert } from "@sazka/design-system/src/modal-alert/mount-alert";
import { logEvent } from "@sazka/utils/logger/use-app-logger";

const LOTTERY_CHECKOUT_TIMEOUT = 20_000;

const checkouts = ref<LotteryCheckout[]>([]);
let depositTimeout = null;
let store: ReturnType<typeof useLotteryCheckoutStore> = null;
let hasInitialized = false;
const activeCheckout = computed(() =>
    checkouts.value.find((x) => x.key === store.checkoutKey && store.betState !== "none")
);
let didBeforeUnload = false;
const isSubsription = computed(() => !!store.subscriptionIds?.subscriptionId);

export const useLotteryCheckoutManager = () => {
    ensureManagerInitialized();

    function ensureManagerInitialized() {
        if (hasInitialized) return;
        hasInitialized = true;

        store = useLotteryCheckoutStore();
        if (store?.betState === "waiting-for-3ds") {
            window.scrollTo({
                top: store.scrollPosition,
                left: 0,
            });

            const unwatch = watch(
                () => store.betState,
                () => {
                    if (store.betState === "waiting-for-3ds") {
                        addEventListener("beforeunload", handleBeforeUnload);
                        addEventListener("pagehide", handleUnload);
                    } else {
                        removeEventListener("beforeunload", handleBeforeUnload);
                        removeEventListener("pagehide", handleUnload);
                        unwatch();
                    }
                },
                { immediate: true }
            );

            const loaderText = isSubsription.value
                ? "Čekáme na připsání peněz na váš účet. Nezavírejte okno, došlo by k přerušení procesu a založení trvalého příkazu by bylo zrušeno."
                : "Čekáme na připsání peněz na váš účet.";

            Loader.showNowAboveAll(loaderText);
            depositTimeout = timeoutHandler(LOTTERY_CHECKOUT_TIMEOUT);
            logSubscriptionEvent("waiting for deposit, FE timeout started");
        }
    }

    function cancelTimeoutAndCheckout() {
        Loader.hide();
        clearTimeout(depositTimeout);
        cancelCheckout();
    }

    function registerCheckoutCallbacks() {
        ng_hs.depositAborted(() => {
            logSubscriptionEvent("ng_hs.depositAborted received -> checkout canceled");
            cancelTimeoutAndCheckout();
        });

        ng_hs.depositTimeout(() => {
            logSubscriptionEvent("ng_hs.depositTimeout received -> checkout canceled");
            cancelTimeoutAndCheckout();
        });

        ng_hs.depositPending(() => {
            store.betState = "deposit-pending";
            activeCheckout.value.continue();
            logSubscriptionEvent("ng_hs.depositPending received");
        });

        ng_hs.onStateChange((state) => {
            if (state === "idle" && store.betState === "waiting-for-3ds") {
                Loader.hide();
                logSubscriptionEvent("ng_hs.onStateChange changed to 'idle' -> checkout canceled");
                cancelCheckout();
            } else if (state === "active" && store.betState === "waiting-for-3ds") {
                Loader.hide();
                clearTimeout(depositTimeout);
            }
        });

        ng_hs.depositSucceeded(() => {
            logSubscriptionEvent("ng_hs.depositSucceeded received");
            clearTimeout(depositTimeout);

            if (store.betState === "waiting-for-deposit") activeCheckout.value.continue();
            if (store.betState === "waiting-for-3ds") {
                Loader.hide();
                if (activeCheckout.value) {
                    if (activeCheckout.value.useAnalytics) {
                        AnalyticsHelper.restoreCart(store.analyticsCart);
                    }
                    activeCheckout.value.restartFlow(store.postData, store.dataForRestore);
                } else {
                    createOrphanCheckout();
                }
            }
        });
    }

    async function onDepositRedirectionStart() {
        if (!activeCheckout.value) {
            return;
        }
        store.betState = "waiting-for-3ds";
        const postData = activeCheckout.value.getData();
        const restoreData = activeCheckout.value.dataForRestore ? activeCheckout.value.dataForRestore() : null;
        store.postData = postData;
        store.dataForRestore = restoreData;
        store.analyticsCart = AnalyticsHelper.cart;
        store.scrollPosition = window.scrollY;
        store.orphanedCheckoutData.httpMethod = activeCheckout.value.httpMethod;
        store.orphanedCheckoutData.url = activeCheckout.value.url;
        store.orphanedCheckoutData.cost = activeCheckout.value.getCost();
        store.orphanedCheckoutData.useDefaultAnalytics = activeCheckout.value.useAnalytics;
        await nextTick();
    }

    function registerCheckout(checkout: LotteryCheckout) {
        checkouts.value.push(checkout);
    }

    function showBetNotPlacedWarning() {
        showAlert({
            title: "Sázka nebyla vsazena",
            subText: "Vklad jsme připsali na váš účet, ale sázka nebyla vsazena. Prosím zkuste to znovu.",
        });
    }

    function cancelCheckout() {
        didBeforeUnload = false;
        activeCheckout.value?.cancel();
        store.$reset();
    }

    function handleBeforeUnload(event) {
        event.preventDefault();
        didBeforeUnload = true;
        event.returnValue = "";
        return "";
    }

    function timeoutHandler(time: number) {
        return setTimeout(() => {
            if (didBeforeUnload) {
                clearTimeout(depositTimeout);
                depositTimeout = timeoutHandler(10000);
                didBeforeUnload = false;
            } else {
                Loader.hide();
                showAlert({
                    title: "Peníze se nepodařilo připsat v limitu",
                });
                logSubscriptionEvent("FE timeout expired -> checkout canceled");
                cancelCheckout();
            }
        }, time);
    }

    function handleUnload() {
        Loader.hide();
        logSubscriptionEvent("page hidden -> checkout canceled");
        cancelCheckout();
    }

    async function createOrphanCheckout() {
        // checkout does not exist, so we have to create new one - orphaned checkout
        const orphanCheckout = new LotteryCheckout({
            key: store.checkoutKey,
            betServiceUrl: store.orphanedCheckoutData.url,
            httpMethod: store.orphanedCheckoutData.httpMethod,
            getCost: () => store.orphanedCheckoutData.cost,
            isRestarted: true,
            getData: () => store.postData,
            useDefaultAnalytics: store.orphanedCheckoutData.useDefaultAnalytics,
        });

        if (store.orphanedCheckoutData.useDefaultAnalytics) {
            AnalyticsHelper.restoreCart(store.analyticsCart);
        }

        const response = await orphanCheckout.start();
        const payload = response.Payload || response.payload;
        const isSyndicate = !!payload?.deadlineToJoin;
        const wagerId = payload?.wagerId || payload?.WagerID || payload?.WagerID;

        if (wagerId) {
            Loader.showNow("Sázka byla úspěšně vsazena.");
            window.location.href = isSyndicate
                ? `/loterie/skupinove-sazeni/detail/${wagerId}`
                : `/moje-sazky/detail/${wagerId}`;
        }
    }

    function logSubscriptionEvent(message: string) {
        if (isSubsription.value) {
            logEvent("subscription", { message });
        }
    }

    return {
        registerCheckoutCallbacks,
        registerCheckout,
        showBetNotPlacedWarning,
        onDepositRedirectionStart,
    };
};
